
import React from "react";
import { CaseStudyLayout } from "@/components/case-studies/CaseStudyLayout";
import { ProcessStep } from "@/components/case-studies/ProcessStep";
import { CaseMetrics } from "@/components/case-studies/CaseMetrics";
import { CalendarCheck, MessageSquare, Phone, Target, UserCheck } from "lucide-react";

const Healthcare = () => {
  const metrics = [
    {
      value: "$13",
      label: "Cost Per Lead",
      description: "Unprecedented efficiency in healthcare lead generation"
    },
    {
      value: "8%",
      label: "No-Show Rate",
      description: "Reduced from industry average of 90%"
    },
    {
      value: "$208",
      label: "Cost Per Customer",
      description: "For a $1,000+ service"
    },
    {
      value: "5x",
      label: "ROI Improvement",
      description: "Compared to previous acquisition methods"
    },
    {
      value: "82%",
      label: "Qualification Rate",
      description: "Of leads that entered our system"
    },
    {
      value: "96%",
      label: "Patient Satisfaction",
      description: "With the initial AI interaction"
    }
  ];

  return (
    <CaseStudyLayout
      title="Transforming Healthcare Patient Acquisition"
      industry="Healthcare"
    >
      <div className="max-w-4xl mx-auto mb-16">
        <p className="text-xl leading-relaxed mb-8 fade-in">
          For our healthcare client, we developed a revolutionary patient acquisition 
          system that generated leads at just $13 each using Meta advertising. Our AI 
          qualification process dramatically reduced the no-show rate from an industry 
          standard of 90% to just 8%, resulting in a cost per acquired customer of only 
          $208 for a service valued at over $1,000.
        </p>
        
        <CaseMetrics metrics={metrics} />
        
        <div className="my-16">
          <h2 className="mb-10 fade-in">Our Process</h2>
          
          <ProcessStep
            number="01"
            title="Targeted Meta Advertising"
            description="We implemented highly targeted Meta ad campaigns designed specifically for healthcare patient acquisition. By leveraging detailed demographic and behavioral data, we were able to reach potential patients with unprecedented precision, resulting in an extremely low cost per lead of just $13."
            image="/healthcare-ads.webp"
            icon={<Target className="h-6 w-6" />}
          />
          
          <ProcessStep
            number="02"
            title="Healthcare-Compliant Lead Capture"
            description="We developed HIPAA-compliant landing pages and form systems that captured essential patient information while maintaining strict privacy standards. These pages were optimized for conversion while gathering the right data points for effective qualification."
            image="/healthcare-landing.webp"
            icon={<UserCheck className="h-6 w-6" />}
            reversed
          />
          
          <ProcessStep
            number="03"
            title="AI-Powered Engagement"
            description="Using our Vail AI system, we engaged with leads through multiple channels, including SMS and calls. Vail was trained on healthcare-specific knowledge to answer common questions, address concerns, and begin the qualification process in a compassionate, informed manner."
            image="/healthcare-engagement.webp"
            icon={<MessageSquare className="h-6 w-6" />}
          />
          
          <ProcessStep
            number="04"
            title="Comprehensive Qualification"
            description="Vail conducted thorough qualification calls to assess patient needs, insurance status, availability, and commitment level. This process filtered out non-serious inquiries and prepared qualified patients for their appointments, dramatically reducing the typically high no-show rates in healthcare."
            image="/healthcare-qualification.webp"
            icon={<Phone className="h-6 w-6" />}
            reversed
          />
          
          <ProcessStep
            number="05"
            title="Appointment Setting & Reminders"
            description="For qualified patients, Vail handled appointment scheduling and implemented an automated reminder system. This included personalized follow-ups via SMS and calls prior to appointments, contributing to the dramatic reduction in no-show rates from 90% to just 8%."
            image="/healthcare-appointment.webp"
            icon={<CalendarCheck className="h-6 w-6" />}
          />
        </div>
        
        <div className="bg-secondary/30 p-8 rounded-2xl my-16 fade-in">
          <h3 className="mb-4">The Results</h3>
          <p className="mb-4">
            Our comprehensive system revolutionized the client's patient acquisition process. 
            With a lead cost of just $13 and a final customer acquisition cost of $208 for a 
            $1,000+ service, we delivered an exceptional ROI that far exceeded industry standards.
          </p>
          <p>
            Most importantly, by reducing the no-show rate from 90% to 8%, we solved one of the 
            most persistent challenges in healthcare marketing. This dramatic improvement not only 
            increased revenue but also optimized staff utilization and improved overall practice 
            efficiency.
          </p>
        </div>
      </div>
    </CaseStudyLayout>
  );
};

export default Healthcare;
